import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';

import { BaseApiService } from '../../../core/base-api.service';

/** Body for POST /api/v1/preview. Tenant is taken from the bearer token, never
 *  sent here. `journeyType` is an optional render hint (defaults server-side). */
export interface PreviewRequest {
  svg: string;
  feedId: string;
  stopId: string;
  journeyType?: string;
}

/** Why a preview render failed, mapped from the endpoint's status codes so the
 *  UI can show a specific message instead of a bare HTTP error. */
export type PreviewErrorKind = 'validation' | 'feed-not-found' | 'render-failed' | 'render-unreachable' | 'unknown';

export class PreviewError extends Error {
  constructor(
    readonly kind: PreviewErrorKind,
    /** Server-supplied reason where one is carried (422/502 body), else ''. */
    readonly detail: string,
    readonly status: number,
  ) {
    super(detail || kind);
    this.name = 'PreviewError';
  }
}

/**
 * Renders a preview PNG of an SVG template against a stop's live cached
 * departures. The frontend talks only to core; core proxies the internal
 * render-service and returns raw `image/png` bytes (not base64, not JSON).
 *
 *   POST /api/v1/preview  { svg, feedId, stopId, journeyType? }  ->  image/png
 *
 * A stop with no recently cached departures still renders — an empty board is
 * expected behaviour, not an error. Real errors map to `PreviewError`:
 *   400 validation · 404 feed-not-found · 422 render-failed · 502 render-unreachable
 */
@Injectable({ providedIn: 'root' })
export class PreviewService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly previewUrl = `${this.coreApiUrl}/api/v1/preview`;

  /** Renders `request` and returns the PNG as a Blob. Caller owns turning it
   *  into an object URL (and revoking it). Fails with a `PreviewError`. */
  render(request: PreviewRequest): Observable<Blob> {
    return this.http
      .post(this.previewUrl, request, { responseType: 'blob', observe: 'response' })
      .pipe(
        map((response) => response.body ?? new Blob([], { type: 'image/png' })),
        catchError((error: HttpErrorResponse) =>
          from(toPreviewError(error)).pipe(switchMap((mapped) => throwError(() => mapped))),
        ),
      );
  }
}

const STATUS_KIND: Record<number, PreviewErrorKind> = {
  400: 'validation',
  404: 'feed-not-found',
  422: 'render-failed',
  502: 'render-unreachable',
};

/** Maps an HttpErrorResponse to a PreviewError, decoding the error body — which
 *  arrives as a Blob because the success path requested `responseType: 'blob'`,
 *  so error bodies come back as Blobs too and must be read asynchronously. The
 *  422/502 bodies carry the render-service's reason as text; we read it so the
 *  UI can show it verbatim. */
async function toPreviewError(error: HttpErrorResponse): Promise<PreviewError> {
  const kind = STATUS_KIND[error.status] ?? 'unknown';
  let detail = '';
  const body = error.error;
  if (body instanceof Blob) {
    try {
      detail = (await body.text()).trim();
    } catch {
      detail = '';
    }
  } else if (typeof body === 'string') {
    detail = body;
  }
  return new PreviewError(kind, detail, error.status);
}
